<template>
  <!-- ==================== 主对话框 ==================== -->
  <el-dialog
    :title="`客服席位管理 - ${workspaceName}`"
    :visible.sync="dialogVisible"
    width="1000px"
    append-to-body
    @close="handleClose"
    destroy-on-close
  >
    <div class="seat-management">
      <!-- 头部操作栏 -->
      <div class="header-actions">
        <div class="tip-info">
          <span class="tip-text">
            席位数量：{{ seatList.length }} / {{ maxSeatCount }}
            <a style="margin-left: 10px;color: #888888;">每个席位消耗一个配额</a>
          </span>
        </div>
        <el-button type="text" size="small" @click="openCreateDialog">
          <svg t="1779467035047" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="5600" width="10" height="10"><path d="M914.618182 477.090909H546.909091V109.381818c0-18.618182-16.290909-34.909091-34.909091-34.909091s-34.909091 16.290909-34.909091 34.909091v367.709091H109.381818c-18.618182 0-34.909091 16.290909-34.909091 34.909091s16.290909 34.909091 34.909091 34.909091h367.709091v367.709091c0 18.618182 16.290909 34.909091 34.909091 34.909091s34.909091-16.290909 34.909091-34.909091V546.909091h367.709091c18.618182 0 34.909091-16.290909 34.909091-34.909091s-16.290909-34.909091-34.909091-34.909091z" fill="#1296db" p-id="5601"></path></svg>
          新建席位
        </el-button>
      </div>

      <!-- 席位列表 -->
      <el-table v-if="!loading && seatList.length > 0" :data="seatList" border stripe v-loading="loading">
        <el-table-column prop="name" label="席位名称" min-width="180">
          <template slot-scope="{ row }">
            <div class="seat-name" style="display: block; width:100%;text-align: center;border: 1px solid white;">
              <span>{{ row.name }}</span>
              <el-tag v-if="!row.is_active" type="info" size="mini">已禁用</el-tag>
              <el-tag v-if="row.seat_status === 'warning'" type="warning" size="mini">告警</el-tag>
              <el-tag v-if="row.seat_status === 'disabled'" type="danger" size="mini">强制禁用</el-tag>
            </div>
          </template>
        </el-table-column>

        <el-table-column prop="workspace_slug" label="绑定知识库" min-width="150">
          <template slot-scope="{ row }">
            <div class="workspace-info" style="display: block; width:100%;text-align: center;border: 1px solid white;">
              <span>{{ getWorkspaceName(row.workspace_slug) }}</span>
              <el-tag size="mini" type="info">{{ getSeatCountByWorkspace(row.workspace_slug) }}个席位</el-tag>
            </div>
          </template>
        </el-table-column>

        <el-table-column prop="channel_type" label="渠道类型" width="100">
          <template slot-scope="{ row }">
            <span style="text-align: center; width: 100%; display: block;">
              <el-tag :type="getChannelTypeTag(row.channel_type)" size="small">
                {{ getChannelTypeName(row.channel_type) }}
              </el-tag>
            </span>
          </template>
        </el-table-column>

        <el-table-column prop="channel_id" label="渠道标识" min-width="150">
          <template slot-scope="{ row }">
            <span style="text-align: center; width: 100%; display: block;">
              <span class="channel-id">{{ row.channel_id || '-' }}</span>
            </span>
          </template>
        </el-table-column>

        <el-table-column label="域名白名单" min-width="150">
          <template slot-scope="{ row }">
            <span style="text-align: center; width: 100%; display: block;">
              <div v-if="row.channel_type === 'web' && row.allowed_domains">
                <el-popover placement="top" width="300" trigger="hover">
                  <div class="domain-list">
                    <div v-for="(domain, idx) in parseDomains(row.allowed_domains)" :key="idx">
                      {{ domain }}
                    </div>
                  </div>
                  <span slot="reference" class="domain-count">
                    {{ parseDomains(row.allowed_domains).length }}个域名
                  </span>
                </el-popover>
              </div>
              <span v-else>-</span>
            </span>
          </template>
        </el-table-column>

        <el-table-column label="请求统计" width="120">
          <template slot-scope="{ row }">
            <div class="stat-info" style="display: block; width:100%;text-align: center;border: 1px solid white;">
              <div>{{ row.request_count || 0 }} 次</div>
              <div class="last-time" v-if="row.last_request_at">
                {{ formatTime(row.last_request_at) }}
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="状态" width="100" align="center">
          <template slot-scope="{ row }">
            <el-switch
              v-model="row.is_active"
              :disabled="row.seat_status === 'disabled'"
              @change="handleToggle(row)"
            />
          </template>
        </el-table-column>

        <el-table-column label="操作" width="120" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" title="嵌入代码" @click="handleGetEmbedCode(row)">
              <svg t="1779467198268" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="9800" width="16" height="16"><path d="M155.708235 558.320941a33.039059 33.039059 0 0 1-23.341176-56.410353l174.983529-174.983529a33.039059 33.039059 0 0 1 46.712471 46.682353l-175.013647 175.013647a33.039059 33.039059 0 0 1-23.341177 9.697882" fill="#1296db" p-id="9801"></path><path d="M714.962824 733.304471a33.069176 33.069176 0 0 1-23.371295-56.380236l174.98353-174.983529a33.039059 33.039059 0 0 1 46.71247 46.682353l-174.772705 175.104c-6.264471 6.204235-14.757647 9.667765-23.552 9.577412m-383.939765 0c-8.734118 0-17.136941-3.403294-23.341177-9.577412l-174.893176-175.104a32.978824 32.978824 0 0 1 13.131294-57.886118c12.257882-2.921412 25.118118 1.415529 33.129412 11.203765l174.983529 175.104a33.069176 33.069176 0 0 1-23.009882 56.259765" fill="#1296db" p-id="9802"></path><path d="M456.914824 742.339765a33.008941 33.008941 0 0 1-31.503059-43.279059l119.597176-373.76a33.039059 33.039059 0 1 1 62.885647 20.148706l-119.506823 374.000941a33.008941 33.008941 0 0 1-31.472941 22.889412m433.031529-184.018824c-8.764235 0-17.167059-3.493647-23.371294-9.697882l-174.98353-174.98353a33.099294 33.099294 0 1 1 46.923295-46.682353l174.772705 174.98353a33.069176 33.069176 0 0 1-23.341176 56.380235" fill="#1296db" p-id="9803"></path></svg>
            </el-button>
            <el-button type="text" size="small" title="编辑" @click="handleEdit(row)">
              <svg t="1779544090239" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="5599" width="12" height="12"><path d="M882.553 207.403l-66.652-66.652L894.352 62.3s67.244-1.399 67.244 65.845l-79.043 79.258z m-727.882 34.214v627.609H782.28V443.348l89.658-89.658v515.536c0 49.518-40.14 89.658-89.658 89.658H154.671c-49.518 0-89.658-40.14-89.658-89.658V241.617c0-49.518 40.14-89.658 89.658-89.658h515.536l-89.658 89.658H154.671zM378.817 645.08v-67.244l33.622-33.622 67.199 67.199-33.578 33.667h-67.243z m458.965-392.789L502.021 588.967 434.853 521.8l336.219-336.219 66.71 66.71z" fill="#1296db" p-id="5600"></path></svg>
            </el-button>
            <el-button type="text" size="small" title="删除" style="color: #f56c6c;" @click="handleDelete(row)">
              <svg t="1779626456168" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="5880" width="12" height="12"><path d="M835.919 836.467c-179.2 179.235-469.839 179.235-649.004 0-179.235-179.2-179.235-469.839 0-649.004 179.235-179.235 469.804-179.235 649.004 0 179.271 179.235 179.271 469.804 0 649.004z m-49.858-599.111c-168.572-168.642-414.897-134.426-549.252 0-168.642 168.607-134.426 414.861 0 549.252 168.607 165.358 414.861 134.462 549.252 0 165.358-165.288 134.462-414.861 0-549.252z m-166.418 436.93l-108.19-108.191-108.191 108.156-54.095-54.06L457.357 512l-108.19-108.191 54.095-54.096 108.191 108.192 108.191-108.191 54.06 54.095L565.548 512l108.156 108.191-54.06 54.096z" fill="#1296db" p-id="5881"></path><path d="M835.919 836.467c-179.2 179.235-469.839 179.235-649.004 0-179.235-179.2-179.235-469.839 0-649.004 179.235-179.235 469.804-179.235 649.004 0 179.271 179.235 179.271 469.804 0 649.004z m-49.858-599.111c-168.572-168.642-414.897-134.426-549.252 0-168.642 168.607-134.426 414.861 0 549.252 168.607 165.358 414.861 134.462 549.252 0 165.358-165.288 134.462-414.861 0-549.252z m-166.418 436.93l-108.19-108.191-108.191 108.156-54.095-54.06L457.357 512l-108.19-108.191 54.095-54.096 108.191 108.192 108.191-108.191 54.06 54.095L565.548 512l108.156 108.191-54.06 54.096z" fill="#1296db" p-id="5882"></path></svg>
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 空状态 -->
      <div v-if="!loading && seatList.length === 0" class="empty-state">
        <i><svg t="1779454705183" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="8641" width="48" height="48"><path d="M627.8 906.3l-5.2-15.1C723.1 857 804.2 779.4 845 678.2l14.8 6c-20.8 51.4-52.7 98.7-92.5 136.9-39.9 38.2-88.2 67.7-139.5 85.2zM149.1 392.7l-15.9-1.6c9.4-95.6 52.8-184.3 122.3-249.6C325.4 75.6 415.9 39.4 510 39.4c93.9 0 184.1 36.1 254.1 101.7 69.4 65 112.9 153.3 122.6 248.6l-15.9 1.6C851.3 199.8 696.2 55.4 510 55.4c-183.9 0-342.4 148.1-360.9 337.3z" fill="#cdcdcd" p-id="8642"></path><path d="M347.3 165.1c-1.3 0-2.6-0.6-3.3-1.8-1.2-1.8-0.7-4.3 1.1-5.5 49.3-32.9 106.3-50.3 164.9-50.3 58.4 0 115.2 17.3 164.4 50 1.8 1.2 2.3 3.7 1.1 5.5-1.2 1.8-3.7 2.3-5.5 1.1-47.9-31.8-103.2-48.6-160-48.6-57 0-112.5 16.9-160.5 49-0.7 0.4-1.4 0.6-2.2 0.6z" p-id="8643" fill="#cdcdcd"></path><path d="M170 692.8H92.6c-36.3 0-65.7-28.7-65.7-63.9V442.6c0-35.2 29.5-63.9 65.7-63.9H170c11.8 0 21.5 9.4 21.5 21v272c0 11.7-9.6 21.1-21.5 21.1zM92.6 394.7c-27.4 0-49.7 21.5-49.7 47.9v186.3c0 26.4 22.3 47.9 49.7 47.9H170c3 0 5.5-2.3 5.5-5v-272c0-2.8-2.5-5-5.5-5H92.6z" fill="#cdcdcd" p-id="8644"></path><path d="M117.1 649.1c-24.4 0-44.3-19.3-44.3-42.9" fill="#cdcdcd" p-id="8645"></path><path d="M117.1 653.1c-26.6 0-48.3-21-48.3-46.9 0-2.2 1.8-4 4-4s4 1.8 4 4c0 21.4 18.1 38.9 40.3 38.9 2.2 0 4 1.8 4 4s-1.8 4-4 4z" p-id="8646" fill="#cdcdcd"></path><path d="M934.2 690.8h-77.5c-11.8 0-21.5-9.4-21.5-21v-272c0-11.6 9.6-21 21.5-21h77.5c36.3 0 65.7 28.7 65.7 63.9V627c0.1 35.1-29.4 63.8-65.7 63.8z m-77.4-298.1c-3 0-5.5 2.3-5.5 5v272c0 2.8 2.5 5 5.5 5h77.5c27.4 0 49.7-21.5 49.7-47.9V440.6c0-26.4-22.3-47.9-49.7-47.9h-77.5z" fill="#cdcdcd" p-id="8647"></path><path d="M599.7 982.8H420.4c-21 0-38-17-38-38v-59.3c0-21 17-38 38-38h179.3c21 0 38 17 38 38v59.3c0 20.9-17 38-38 38zM420.4 863.4c-12.1 0-22 9.9-22 22v59.3c0 12.1 9.9 22 22 22h179.3c12.1 0 22-9.9 22-22v-59.3c0-12.1-9.9-22-22-22H420.4zM511 729.8c-37 0-73.3-9.9-105-28.7-9.4-5.6-18.4-12-26.8-19-3.4-2.8-3.9-7.9-1-11.3 2.8-3.4 7.9-3.9 11.3-1 7.8 6.5 16.1 12.4 24.8 17.5 29.2 17.3 62.7 26.5 96.9 26.5 44.9 0 88.5-16 122.7-44.9 3.4-2.9 8.4-2.4 11.3 0.9 2.9 3.4 2.4 8.4-0.9 11.3-37.3 31.4-84.6 48.7-133.3 48.7z" fill="#cdcdcd" p-id="8648"></path><path d="M151 594.2c2.2 0 4 1.8 4 4v54.7c0 2.2-1.8 4-4 4s-4-1.8-4-4v-54.7c0-2.2 1.7-4 4-4zM535 941c0-2.2 1.8-4 4-4h54.7c2.2 0 4 1.8 4 4s-1.8 4-4 4H539c-2.2 0-4-1.8-4-4zM566.3 911.2c0-2.2 1.8-4 4-4h23.3c2.2 0 4 1.8 4 4s-1.8 4-4 4h-23.3c-2.2 0-4-1.8-4-4zM151 547c2.2 0 4 1.8 4 4v23.7c0 2.2-1.8 4-4 4s-4-1.8-4-4V551c0-2.2 1.7-4 4-4zM880.8 594.2c2.2 0 4 1.8 4 4v54.7c0 2.2-1.8 4-4 4s-4-1.8-4-4v-54.7c0-2.2 1.8-4 4-4zM880.8 547c2.2 0 4 1.8 4 4v23.7c0 2.2-1.8 4-4 4s-4-1.8-4-4V551c0-2.2 1.8-4 4-4z" p-id="8649" fill="#cdcdcd"></path><path d="M910.7 649.1c24.4 0 44.3-19.3 44.3-42.9" fill="#cdcdcd" p-id="8650"></path><path d="M910.7 653.1c-2.2 0-4-1.8-4-4s1.8-4 4-4c22.2 0 40.3-17.4 40.3-38.9 0-2.2 1.8-4 4-4s4 1.8 4 4c0 25.9-21.7 46.9-48.3 46.9z" p-id="8651" fill="#cdcdcd"></path><path d="M783.5 463c-0.3 0-0.6 0-0.9-0.1-44.1-5-78.3-53.1-99.2-92.6-16.1-30.4-27.8-62.3-33.8-84.2-24.6 39.8-57.3 71.4-97.3 94.1-36.8 20.8-79.8 34.2-127.8 39.8-81.4 9.4-148.2-6.7-151.1-7.4a8.1 8.1 0 0 1-5.9-9.7 8.1 8.1 0 0 1 9.7-5.9c0.8 0.2 67.4 16.2 145.9 7 104.3-12.2 179.4-60.7 223-144 1.7-3.2 5.3-4.9 8.8-4.1 3.5 0.8 6.1 3.8 6.3 7.4 0.7 12.1 13.8 57 36.4 99.6 27 51 57.8 80.9 86.8 84.1 4.4 0.5 7.5 4.5 7.1 8.8-0.5 4.2-4 7.2-8 7.2z" fill="#cdcdcd" p-id="8652"></path></svg></i>
        <p>暂无客服席位</p>
        <p class="empty-tip">点击上方按钮创建第一个客服席位</p>
      </div>
    </div>

    <!-- ==================== 模式选择对话框 ==================== -->
    <el-dialog 
      title="选择部署模式" 
      :visible.sync="modeSelectVisible" 
      width="700px" 
      append-to-body
      :close-on-click-modal="false"
      @close="resetModeSelect"
    >
      <div class="mode-selector">
        <div 
          class="mode-card" 
          :class="{ active: selectedMode === 'promotion' }"
          @click="selectedMode = 'promotion'"
        >
          <div class="mode-icon">
            <div class="mode-letter-icon promotion">宣</div>
          </div>
          <h3>品牌宣传模式</h3>
          <p>多个渠道共用同一个知识库</p>
          <div class="mode-detail">
            <div class="detail-item">✓ 官网 + 公众号 + 小程序</div>
            <div class="detail-item">✓ 内容统一管理</div>
            <div class="detail-item">✓ 节省知识库配额</div>
          </div>
          <div class="mode-tags">
            <el-tag size="small" type="success">推荐：品牌方</el-tag>
          </div>
        </div>

        <div 
          class="mode-card" 
          :class="{ active: selectedMode === 'store' }"
          @click="selectedMode = 'store'"
        >
          <div class="mode-icon">
            <div class="mode-letter-icon store">店</div>
          </div>
          <h3>店铺独立模式</h3>
          <p>每个渠道拥有独立知识库</p>
          <div class="mode-detail">
            <div class="detail-item">✓ 北京店 + 上海店 + 广州店</div>
            <div class="detail-item">✓ 内容各自独立</div>
            <div class="detail-item">✓ 支持差异化运营</div>
          </div>
          <div class="mode-tags">
            <el-tag size="small" type="warning">推荐：连锁门店</el-tag>
          </div>
        </div>
      </div>

      <div class="quota-info">
        <div class="quota-row">
          <span class="quota-label">📊 当前配额：</span>
          <span>知识库 {{ workspaceList.length }} / {{ maxWorkspaceCount }} 个</span>
          <span style="margin-left: 20px;">席位 {{ seatList.length }} / {{ maxSeatCount }} 个</span>
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button type="primary" @click="goToCreateForm">下一步</el-button>
        <el-button @click="modeSelectVisible = false">取消</el-button>
      </div>
    </el-dialog>

    <!-- ==================== 创建席位表单对话框 ==================== -->
    <el-dialog
      :title="createDialogTitle"
      :visible.sync="formVisible"
      width="1100px"
      append-to-body
      :close-on-click-modal="false"
      @close="resetForm"
    >
      <el-form ref="seatForm" :model="formData" :rules="formRules" label-width="110px">
        <el-row :gutter="10" class="mb8">        
          <el-col :span="12">
            <el-form-item label="席位名称" prop="name" required>
              <el-input 
                v-model="formData.name" 
                placeholder="例如：官网客服" 
                maxlength="50" 
                show-word-limit 
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">       
            <!-- 宣传模式：选择已有知识库 -->
            <template v-if="!isEdit && selectedMode === 'promotion'">
              <el-form-item label="绑定知识库" prop="workspace_slug" required>
                <el-select v-model="formData.workspace_slug" placeholder="请选择知识库" style="width: 100%;">
                  <el-option
                    v-for="kb in workspaceList"
                    :key="kb.slug"
                    :label="`${kb.name} (${kb.document_count || 0}个文档)`"
                    :value="kb.slug"
                  />
                </el-select>
                <p class="form-tip">选择已有知识库，多个席位可共用同一知识库</p>
              </el-form-item>
            </template>

            <!-- 店铺模式：创建新知识库 -->
            <template v-if="!isEdit && selectedMode === 'store'">
              <el-form-item label="新知识库名称" prop="newWorkspaceName" required>
                <el-input 
                  v-model="newWorkspaceName" 
                  placeholder="例如：北京店专属知识库" 
                />
                <p class="form-tip">系统将自动创建新知识库并绑定到此席位</p>
              </el-form-item>
            </template>

            <!-- 编辑时显示当前绑定的知识库（只读） -->
            <template v-if="isEdit">
              <el-form-item label="绑定知识库">
                <el-input :value="getWorkspaceName(formData.workspace_slug)" disabled />
              </el-form-item>
            </template>
          </el-col>
        </el-row>

        <el-row :gutter="10" class="mb8">
          <el-col :span="12">
            <el-form-item label="渠道类型" prop="channel_type" required>
              <el-select v-model="formData.channel_type" placeholder="请选择渠道类型" style="width: 100%;" @change="onChannelTypeChange">
                <el-option label="网站(Web)" value="web" />
                <el-option label="微信公众号" value="wechat" disabled />
                <el-option label="微信小程序" value="miniprogram" disabled />
                <el-option label="移动APP" value="app" disabled />
                <el-option label="淘宝店铺" value="taobao" disabled />
              </el-select>
              <p class="form-tip" v-if="formData.channel_type !== 'web'">其他渠道类型开发中，敬请期待</p>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="渠道标识" prop="channel_id" required>
              <el-input v-model="formData.channel_id" :placeholder="channelIdPlaceholder" />
              <p class="form-tip">{{ channelIdTip }}</p>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- Web渠道专属配置 -->
        <template v-if="formData.channel_type === 'web'">
          <el-form-item label="域名白名单" prop="allowed_domains" required>
            <div class="domain-input-wrapper">
              <div class="domain-tags">
                <div 
                  v-for="(domain, index) in domainList" 
                  :key="index"
                  class="domain-tag"
                >
                  <span class="domain-text">{{ domain }}</span>
                  <span class="domain-close" @click="removeDomain(index)">✕</span>
                </div>
                <span v-if="domainList.length === 0" class="domain-placeholder">暂无域名，请添加</span>
              </div>
              <div class="domain-add">
                <input 
                  type="text"
                  v-model="newDomain"
                  class="domain-input"
                  placeholder="输入域名后按回车添加，例如：example.com"
                  @keyup.enter="addDomain"
                />
                <button type="button" class="domain-add-btn" @click="addDomain">添加</button>
              </div>
            </div>
            <p class="form-tip">支持通配符 *.example.com 匹配子域名，不需要添加 http:// 或 https://</p>
          </el-form-item>

          <el-row :gutter="10" class="mb8">
            <el-col :span="16">
              <el-form-item label="主题色" prop="theme_color">
                <div class="theme-color-picker">
                  <div 
                    v-for="color in presetColors" 
                    :key="color"
                    class="color-option"
                    :class="{ active: formData.theme_color === color }"
                    :style="{ backgroundColor: color }"
                    @click="formData.theme_color = color"
                  >
                    <span v-if="formData.theme_color === color" class="color-active-dot"></span>
                  </div>
                  <div class="custom-color-wrapper">
                    <input 
                      type="color" 
                      v-model="formData.theme_color" 
                      class="color-input-native"
                      title="自定义颜色"
                    />
                    <span class="color-value">{{ formData.theme_color }}</span>
                  </div>
                </div>
              </el-form-item>
            </el-col>
            <el-col :span="8"> 
              <el-form-item label="图标样式" prop="icon_style">
                <el-radio-group v-model="formData.icon_style">
                  <el-radio label="default">默认</el-radio>
                  <el-radio label="round">圆形</el-radio>
                  <el-radio label="square">方形</el-radio>
                </el-radio-group>
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="10" class="mb8">
            <el-col :span="8">
              <el-form-item label="客服头像：" prop="name">
                <div class="upload-container">
                  <div v-if="!formData.icon_file && !previewImage_icon" class="upload-area">
                    <input
                      type="file"
                      id="uploadFile_icon"
                      ref="uploadFile_icon"
                      accept=".jpg,.jpeg,.png,.gif,.bmp,.webp"
                      @change="handleFileUpload_icon"
                      class="file-input"
                    />
                    <label for="uploadFile_icon" class="upload-box">
                      <div class="upload-placeholder">
                        <span class="plus-icon">+</span>
                        <span class="upload-text">点击上传</span>
                      </div>
                    </label>
                  </div>
                  <div v-else class="preview-area">
                    <img :src="previewImage_icon" class="thumbnail-icon" alt="客服头像"/>
                    <button type="button" class="remove-btn" @click.stop="removeFile_icon">×</button>
                  </div>
                </div>
              </el-form-item>
            </el-col>
            <el-col :span="16">
              <el-form-item label="广告图：" prop="name">
                <div class="upload-container">
                  <div v-if="!formData.banner_file && !previewImage_banner" class="upload-area">
                    <input
                      type="file"
                      id="uploadFile_banner"
                      ref="uploadFile_banner"
                      accept=".jpg,.jpeg,.png,.gif,.bmp,.webp"
                      @change="handleFileUpload_banner"
                      class="file-input"
                    />
                    <label for="uploadFile_banner" class="upload-box">
                      <div class="upload-placeholder">
                        <span class="plus-icon">+</span>
                        <span class="upload-text">点击上传</span>
                      </div>
                    </label>
                  </div>
                  <div v-else class="preview-area">
                    <img :src="previewImage_banner" style="width: 400px; height: 80px;" alt="广告图"/>
                    <button type="button" class="remove-btn-banner" @click.stop="removeFile_banner">×</button>
                  </div>
                </div>
              </el-form-item>
            </el-col>
          </el-row>

          <!-- 添加上传进度条 -->
          <el-row v-if="isUploading || uploadProgress > 0" :gutter="10" class="mb8">
            <el-col :span="24">
              <el-form-item label="上传进度：">
                <el-progress
                  :percentage="uploadProgress"
                  :status="uploadError ? 'exception' : (uploadProgress === 100 ? 'success' : undefined)"
                  :stroke-width="8"
                  :show-text="true"
                />
                <div class="upload-status">
                  <div class="uploading-text" v-if="isUploading && uploadProgress < 100">
                    <i class="el-icon-loading"></i> 文件上传中，请稍候...
                  </div>
                  <div class="success-text" v-else-if="uploadProgress === 100">
                    <i class="el-icon-success"></i> 上传完成！
                  </div>
                  <div class="error-text" v-else-if="uploadError">
                    <i class="el-icon-error"></i> {{ uploadError }}
                  </div>
                </div>
              </el-form-item>
            </el-col>
          </el-row>
        </template>
      </el-form>

      <div slot="footer" class="dialog-footer">
        <el-button type="primary" @click="submitForm" :loading="submitLoading">确 定</el-button>
        <el-button @click="formVisible = false">取 消</el-button>
      </div>
    </el-dialog>

    <!-- ==================== 嵌入代码对话框 ==================== -->
    <el-dialog title="嵌入代码" :visible.sync="embedCodeVisible" width="700px" append-to-body>
      <div class="embed-code-container">
        <p class="embed-tip">将以下代码复制到您网站的 &lt;/body&gt; 标签前：</p>
        <pre class="embed-code"><code>{{ embedCode }}</code></pre>
        <el-button type="primary" size="small" @click="copyEmbedCode">
          <svg t="1779470570972" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="5629" width="12" height="12"><path d="M890.197333 41.984H347.178667C296.832 41.984 256 81.194667 256 129.536v75.861333h86.016V156.928c0-16.128 13.610667-29.184 30.378667-29.184h492.16c16.768 0 30.378667 13.056 30.378666 29.184v539.349333c0 7.68-3.2 15.061333-8.96 20.437334a30.378667 30.378667 0 0 1-21.461333 8.149333H733.866667v85.12h156.245333c50.133333 0 90.88-38.826667 91.178667-86.954667V129.536C981.333333 81.194667 940.501333 41.984 890.197333 41.984z" fill="#ffffff" p-id="5630"></path><path d="M676.864 214.016H133.845333C83.498667 214.016 42.666667 253.226667 42.666667 301.568v75.861333h86.016V328.96c0-16.128 13.610667-29.184 30.378666-29.184h492.16c16.768 0 30.378667 13.056 30.378667 29.184v539.349333c0 7.68-3.2 15.061333-8.96 20.437334a30.378667 30.378667 0 0 1-21.461333 8.149333H520.533333v85.12h156.245334c50.133333 0 90.88-38.826667 91.178666-86.954667V301.568c0.042667-48.341333-40.789333-87.552-91.093333-87.552z" fill="#ffffff" p-id="5631"></path><path d="M42.709333 301.568v593.493333c0.298667 48.128 41.045333 86.954667 91.178667 86.954667H546.133333v-85.12H159.445333a30.378667 30.378667 0 0 1-21.461333-8.149333 27.946667 27.946667 0 0 1-8.96-20.437334V328.96c0-16.128 13.610667-29.184 30.378667-29.184h492.16c16.768 0 30.378667 13.056 30.378666 29.184v48.469333H768V301.568c0-48.341333-40.832-87.552-91.178667-87.552H133.802667c-50.304 0-91.136 39.210667-91.093334 87.552z" fill="#ffffff" p-id="5632"></path><path d="M256 384.682667h298.666667a42.666667 42.666667 0 0 1 0 85.333333H256a42.666667 42.666667 0 0 1 0-85.333333zM256 555.349333h298.666667a42.666667 42.666667 0 0 1 0 85.333334H256a42.666667 42.666667 0 0 1 0-85.333334zM256 726.016h298.666667a42.666667 42.666667 0 0 1 0 85.333333H256a42.666667 42.666667 0 0 1 0-85.333333z" fill="#ffffff" p-id="5633"></path></svg>
          复制代码
        </el-button>
      </div>
    </el-dialog>
  </el-dialog>
  <!-- ==================== 主对话框结束 ==================== -->
</template>

<script>
import { 
  getSeatList, 
  createSeatWithExisting, 
  createSeatWithNew, 
  updateSeat, 
  deleteSeat, 
  toggleSeat
} from '@/api/aics/seat';
import { listKbWorkspaces_by_tenantId } from '@/api/aics/kbWorkspace';
import { mapState } from 'vuex';

export default {
  name: 'SeatManagement',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    workspaceSlug: {
      type: String,
      default: ''
    },
    workspaceName: {
      type: String,
      default: ''
    },
    maxSeatCount: {
      type: Number,
      default: 10
    },
    maxWorkspaceCount: {
      type: Number,
      default: 3
    }
  },
  data() {
    return {
      dialogVisible: false,
      loading: false,
      submitLoading: false,
      seatList: [],
      workspaceList: [],
      
      // 模式选择
      modeSelectVisible: false,
      selectedMode: 'promotion',
      previewImage_icon: null,
      previewImage_banner: null,      
      isUploading: false,
      uploadProgress: 0,
      uploadError: null,
      
      // 表单
      formVisible: false,
      isEdit: false,
      currentSeatId: null,
      newWorkspaceName: '',
      domainList: [],
      
      formData: {
        name: '',
        workspace_slug: '',
        channel_type: 'web',
        channel_id: '',
        allowed_domains: '',
        theme_color: '#409EFF',
        icon_style: 'default',
        is_active: true, 
        icon_file: null,
        banner_file: null,
      },
      newDomain: '',  
      presetColors: ['#409EFF', '#67C23A', '#E6A23C', '#F56C6C', '#9B59B6', '#1ABC9C', '#FD79A8', '#2C3E50'],
      
      formRules: {
        name: [
          { required: true, message: '请输入席位名称', trigger: 'blur' },
          { max: 50, message: '不超过50个字符', trigger: 'blur' }
        ],
        workspace_slug: [
          { required: true, message: '请选择知识库', trigger: 'change' }
        ],
        channel_type: [
          { required: true, message: '请选择渠道类型', trigger: 'change' }
        ],
        channel_id: [
          { required: true, message: '请输入渠道标识', trigger: 'blur' }
        ]
      },
      
      embedCodeVisible: false,
      embedCode: ''
    };
  },
  computed: {
    ...mapState('auth', ['tenantInfo', 'userInfo']),
    tenantId() {
      return this.tenantInfo?.tenantId || null;
    },
    userName() {
      return this.userInfo?.username || null;
    },
    createDialogTitle() {
      if (this.isEdit) return '编辑客服席位';
      return this.selectedMode === 'promotion' ? '创建席位（品牌宣传模式）' : '创建席位（店铺独立模式）';
    },
    channelIdPlaceholder() {
      const placeholders = {
        web: '例如：example.com',
        wechat: '例如：gh_xxxxxx',
        miniprogram: '例如：wx1234567890abcdef',
        app: '例如：com.example.app',
        taobao: '例如：shop123456789'
      };
      return placeholders[this.formData.channel_type] || '请输入渠道标识';
    },
    channelIdTip() {
      const tips = {
        web: '网站域名，用于请求来源校验',
        wechat: '微信公众号原始ID',
        miniprogram: '微信小程序的AppID',
        app: 'APP的包名（Bundle ID）',
        taobao: '淘宝店铺ID'
      };
      return tips[this.formData.channel_type] || '';
    }
  },
  watch: {
    visible: {
      handler(val) {
        this.dialogVisible = val;
        if (val) {
          this.loadSeatList();
          this.loadWorkspaceList();
        }
      },
      immediate: true
    }
  },
  methods: {
    addDomain() {
      if (!this.newDomain.trim()) {
        return;
      }
      const domain = this.newDomain.trim().toLowerCase();
      if (this.domainList.includes(domain)) {
        this.$message.warning('域名已存在');
        return;
      }
      this.domainList.push(domain);
      this.formData.allowed_domains = JSON.stringify(this.domainList);
      this.newDomain = '';
      
      if (this.$refs.seatForm) {
        this.$refs.seatForm.validateField('allowed_domains');
      }
    },
    
    removeDomain(index) {
      this.domainList.splice(index, 1);
      this.formData.allowed_domains = JSON.stringify(this.domainList);
      if (this.$refs.seatForm) {
        this.$refs.seatForm.validateField('allowed_domains');
      }
    },

    removeFile_icon(event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      this.formData.icon_file = null;
      this.previewImage_icon = null;
      if (this.$refs.uploadFile_icon) {
        this.$refs.uploadFile_icon.value = '';        
      }
    },
    
    removeFile_banner(event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      this.formData.banner_file = null;
      this.previewImage_banner = null;
      if (this.$refs.uploadFile_banner) {
        this.$refs.uploadFile_banner.value = '';        
      }
    },
    
    async loadSeatList() {
      if (!this.tenantId) return;
      this.loading = true;
      try {
        const res = await getSeatList(this.workspaceSlug, this.tenantId);
        console.log("getSeatList", res);
        if (res.data.code === 200) {
          this.seatList = res.data.data || [];
        }
      } catch (error) {
        console.error('获取席位列表失败:', error);
      } finally {
        this.loading = false;
      }
    },

    async loadWorkspaceList() {
      if (!this.tenantId) return;
      try {
        const res = await listKbWorkspaces_by_tenantId(this.tenantId);
        if (res.code === 200) {
          this.workspaceList = res.data || [];
        }
      } catch (error) {
        console.error('获取知识库列表失败:', error);
      }
    },

    openCreateDialog() {
      if (this.seatList.length >= this.maxSeatCount) {
        this.$message.warning(`席位数量已达上限（${this.maxSeatCount}个），请升级套餐或删除其他席位`);
        return;
      }
      this.modeSelectVisible = true;
      this.selectedMode = 'promotion';
    },

    goToCreateForm() {
      this.modeSelectVisible = false;
      this.isEdit = false;
      this.currentSeatId = null;
      this.newWorkspaceName = '';
      this.resetForm();
      
      if (this.selectedMode === 'promotion' && this.workspaceSlug) {
        this.formData.workspace_slug = this.workspaceSlug;
      }
      
      this.formVisible = true;
    },

    resetModeSelect() {
      this.selectedMode = 'promotion';
    },

    handleEdit(row) {
      console.log("handleEdit", row);
      this.isEdit = true;
      this.currentSeatId = row.id;
      this.formData = {
        name: row.name,
        workspace_slug: row.workspace_slug,
        channel_type: row.channel_type || 'web',
        channel_id: row.channel_id || '',
        allowed_domains: row.allowed_domains || '',
        theme_color: row.theme_color || '#409EFF',
        icon_style: row.icon_style || 'default',        
        is_active: row.is_active,
        icon_file: null,
        banner_file: null
      };
      this.domainList = this.parseDomains(row.allowed_domains);
      this.previewImage_icon = row.icon_url;
      this.previewImage_banner = row.banner_url;
      this.formVisible = true;
    },

    async handleDelete(row) {
      try {
        await this.$confirm(`确定要删除席位「${row.name}」吗？删除后不可恢复。`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        });
        
        const res = await deleteSeat(row.id, this.tenantId);
        if (res.data.code === 200) {
          this.$message.success('删除成功');
          this.loadSeatList();
        } else {
          this.$message.error(res.data.message || '删除失败');
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除失败:', error);
          this.$message.error('删除失败');
        }
      }
    },

    async handleToggle(row) {
      try {
        const res = await toggleSeat(row.id, this.tenantId, row.is_active);
        console.log("toggleSeat res", res);
        if (res.data.code === 200) {
          this.$message.success(row.is_active ? '已启用' : '已禁用');
        } else {
          this.$message.error(res.data.message || '操作失败');
          row.is_active = !row.is_active;
        }
      } catch (error) {
        console.error('切换状态失败:', error);
        this.$message.error('操作失败');
        row.is_active = !row.is_active;
      }
    },

    async handleGetEmbedCode(row) {
      try {
        const embedCode = this.generateEmbedCode(row);
        this.embedCode = embedCode;
        this.embedCodeVisible = true;
      } catch (error) {
        console.error('生成嵌入代码失败:', error);
        this.$message.error('生成嵌入代码失败');
      }
    },

    // ========== 生成嵌入代码 ==========
    generateEmbedCode(seat) {
      console.info("generateEmbedCode", seat);
      const platformUrl = window.location.origin;
      const escapedName = seat.name.replace(/'/g, "\\'");
      
      const iconUrl = seat.icon_url || '';
      const bannerUrl = seat.banner_url || '';
      
      // 构建配置对象
      const config = {
        workspaceSlug: seat.workspace_slug,
        workspaceName: escapedName,
        seatId: seat.id,
        tenantId: seat.tenant_id,
        username: '',
        themeColor: seat.theme_color || '#409eff',
        iconStyle: seat.icon_style || 'default',
        position: seat.position || 'right',
        iconUrl: iconUrl,
        bannerUrl: bannerUrl
      };
      
      const configJson = JSON.stringify(config, null, 2);
      
      // 使用 Unicode 转义，完全避免 ESLint 和 Vue 编译器问题
      // \u003C = <, \u003E = >
      return '<!-- AI客服系统嵌入代码 -->\n' +
        '\u003Cscript\u003E\n' +
        '  window.AicsChat = {\n' +
        '    config: ' + configJson + '\n' +
        '  };\n' +
        '  (function() {\n' +
        '    var s = document.createElement("script");\n' +
        '    s.src = "' + platformUrl + '/embed.js";\n' +
        '    s.defer = true;\n' +
        '    document.head.appendChild(s);\n' +
        '  })();\n' +
        '\u003C/script\u003E\n' +
        '<!-- AI客服系统嵌入代码结束 -->';
    },
    // ========== 生成嵌入代码结束 ==========

    copyEmbedCode() {
      const textarea = document.createElement('textarea');
      textarea.value = this.embedCode;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      this.$message.success('嵌入代码已复制到剪贴板');
    },

    async submitForm() {
      console.log("this.formData", this.formData);
      console.log("this.domainList", this.domainList);
      
      if (this.formData.channel_type === 'web') {
        if (!this.domainList || this.domainList.length === 0) {
          this.$message.warning('请至少添加一个域名白名单');
          return;
        }
        this.formData.allowed_domains = JSON.stringify(this.domainList);
      } else {
        this.formData.allowed_domains = '';
      }

      this.$refs.seatForm.validate(async (valid) => {
        if (!valid) return;
        
        this.submitLoading = true;
        try {            
          let res;
          console.log("this.currentSeatId", this.currentSeatId);
          console.log("this.formData", this.formData);
          
          if (this.isEdit) {
            const updateData = {
              id: this.currentSeatId,
              name: this.formData.name,
              workspace_slug: this.formData.workspace_slug,
              channel_id: this.formData.channel_id,
              allowed_domains: this.formData.allowed_domains,
              is_active: this.formData.is_active,
              theme_color: this.formData.theme_color,
              icon_style: this.formData.icon_style,
              icon_file: this.formData.icon_file,
              banner_file: this.formData.banner_file
            };
            console.log("updateSeat-updateData", updateData);
            res = await updateSeat(updateData, this.tenantId);
          } else if (this.selectedMode === 'promotion') {
            const createData = {
              name: this.formData.name,
              workspace_slug: this.formData.workspace_slug,
              channel_type: this.formData.channel_type,
              channel_id: this.formData.channel_id,
              allowed_domains: this.formData.allowed_domains,
              theme_color: this.formData.theme_color,
              icon_style: this.formData.icon_style
            };
            res = await createSeatWithExisting(createData, this.tenantId, this.userName);
          } else {
            const createData = {
              name: this.formData.name,
              channel_type: this.formData.channel_type,
              channel_id: this.formData.channel_id,
              allowed_domains: this.formData.allowed_domains,
              theme_color: this.formData.theme_color,
              icon_style: this.formData.icon_style
            };
            res = await createSeatWithNew(createData, this.newWorkspaceName,
              this.tenantId, this.userName, this.formData.icon_file, this.formData.banner_file);
            console.info("createSeatWithNew", res);
          }
          
          if (res.data.code === 200) {
            this.$message.success(this.isEdit ? '更新成功' : '创建成功');
            this.formVisible = false;
            this.loadSeatList();
            this.loadWorkspaceList();
          } else {
            this.$message.error(res.data.message || (this.isEdit ? '更新失败' : '创建失败'));
          }
        } catch (error) {
          console.error('提交失败:', error);
          this.$message.error(error.message || '操作失败');
        } finally {
          this.submitLoading = false;
        }
      });
    },

    onChannelTypeChange() {
      this.formData.channel_id = '';
      if (this.formData.channel_type !== 'web') {
        this.domainList = [];
      }
    },

    onDomainChange(val) {
      this.domainList = val;
    },

    parseDomains(domainsJson) {
      if (!domainsJson) return [];
      try {
        return JSON.parse(domainsJson);
      } catch (e) {
        return [];
      }
    },

    resetForm() {
      this.formData = {
        name: '',
        workspace_slug: '',
        channel_type: 'web',
        channel_id: '',
        allowed_domains: '',
        theme_color: '#409EFF',
        icon_style: 'default',
        is_active: true,
        icon_file: null,
        banner_file: null
      };
      this.domainList = [];
      this.newWorkspaceName = '';
      this.previewImage_icon = null;
      this.previewImage_banner = null;
      if (this.$refs.seatForm) {
        this.$refs.seatForm.clearValidate();
      }
    },

    handleClose() {
      this.dialogVisible = false;
      this.$emit('update:visible', false);
    },

    getWorkspaceName(slug) {
      console.info("getWorkspaceName", slug, this.workspaceList);
      const workspace = this.workspaceList.find(w => w.slug === slug);
      return workspace ? workspace.name : slug;
    },

    getSeatCountByWorkspace(slug) {
      console.info("getSeatCountByWorkspace", slug, this.seatList);
      return this.seatList.filter(s => s.workspace_slug === slug).length;
    },

    getChannelTypeName(type) {
      const map = {
        web: '网页',
        wechat: '公众号',
        miniprogram: '小程序',
        app: 'APP',
        taobao: '淘宝'
      };
      return map[type] || type;
    },

    isImageFile(file) {
      return file && file.type.match('image.*');
    },

    getChannelTypeTag(type) {
      const map = {
        web: 'primary',
        wechat: 'success',
        miniprogram: 'success',
        app: 'warning',
        taobao: 'danger'
      };
      return map[type] || 'info';
    },

    getContrastColor(hexColor) {
      if (!hexColor) return '#fff';
      let hex = hexColor.replace('#', '');
      if (hex.length === 3) {
        hex = hex.split('').map(c => c + c).join('');
      }
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      return brightness > 128 ? '#333' : '#fff';
    },

    handleFileUpload_icon(event) {
      const file = event.target.files[0];
      console.log("handleFileUpload_icon", file);
      if (file) {
        if (file.size > 5 * 1024 * 1024) {
          this.$message.error('文件大小不能超过5MB');
          event.target.value = '';
          return;
        }
        this.formData.icon_file = file;
        console.log("this.formData.icon_file", this.formData.icon_file);
        if (this.isImageFile(file)) {
          const reader = new FileReader();
          reader.onload = (e) => {
            this.previewImage_icon = e.target.result;
            console.log("this.previewImage_icon", this.previewImage_icon);
          };
          reader.readAsDataURL(file);
        } else {
          this.previewImage_icon = null;
        }
      }
    },

    handleFileUpload_banner(event) {
      const file = event.target.files[0];
      console.log("handleFileUpload_banner", file);
      if (file) {
        if (file.size > 5 * 1024 * 1024) {
          this.$message.error('文件大小不能超过5MB');
          event.target.value = '';
          return;
        }
        this.formData.banner_file = file;
        console.log("this.formData.banner_file", this.formData.banner_file);
        if (this.isImageFile(file)) {
          const reader = new FileReader();
          reader.onload = (e) => {
            this.previewImage_banner = e.target.result;
            console.log("this.previewImage_banner", this.previewImage_banner);
          };
          reader.readAsDataURL(file);
        } else {
          this.previewImage_banner = null;
        }
      }
    },

    formatTime(time) {
      if (!time) return '';
      const date = new Date(time);
      return `${date.getMonth() + 1}/${date.getDate()} ${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`;
    }
  }
};
</script>

<style scoped>
/* ... 样式保持不变 ... */
.seat-management {
  min-height: 400px;
}

.header-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.tip-info {
  font-size: 13px;
  color: #909399;
}

.tip-info i {
  margin-left: 4px;
  cursor: help;
}

.seat-name {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.workspace-info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.domain-count {
  color: #409eff;
  cursor: pointer;
  font-size: 12px;
}

.domain-list {
  max-height: 200px;
  overflow-y: auto;
}

.domain-list div {
  padding: 4px 0;
  font-size: 12px;
  font-family: monospace;
}

.stat-info {
  font-size: 12px;
  line-height: 1.5;
}

.last-time {
  color: #909399;
  font-size: 11px;
}

.empty-state {
  text-align: center;
  padding: 60px 0;
  color: #909399;
}

.empty-state i {
  font-size: 64px;
  margin-bottom: 16px;
}

.empty-state p {
  margin: 8px 0;
}

.empty-tip {
  font-size: 12px;
}

/* 模式选择样式 */
.mode-selector {
  display: flex;
  gap: 20px;
  padding: 10px 0 20px;
}

.mode-card {
  flex: 1;
  border: 2px solid #e4e7ed;
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s;
  text-align: center;
}

.mode-card:hover {
  border-color: #409eff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.mode-card.active {
  border-color: #409eff;
  background: linear-gradient(135deg, #ecf5ff 0%, #f0f9ff 100%);
}

.mode-letter-icon {
  width: 60px;
  height: 60px;
  line-height: 60px;
  text-align: center;
  font-size: 28px;
  font-weight: bold;
  border-radius: 50%;
  margin: 0 auto 16px;
  display: inline-block;
}

.mode-letter-icon.promotion {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.mode-letter-icon.store {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(240, 147, 251, 0.3);
}

.mode-card h3 {
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 500;
}

.mode-card p {
  color: #606266;
  font-size: 13px;
  margin-bottom: 16px;
}

.mode-detail {
  text-align: left;
  background: #f8f9fa;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
}

.detail-item {
  font-size: 12px;
  color: #606266;
  line-height: 1.8;
}

.mode-tags {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.quota-info {
  background: #f5f7fa;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 13px;
  color: #606266;
  margin-top: 16px;
}

.quota-row {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.quota-label {
  font-weight: 500;
}

/* 表单样式 */
.form-tip {
  font-size: 12px;
  color: #909399;
  margin: 4px auto 0 auto;
}

.theme-color-picker {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.color-option {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.color-option:hover {
  transform: scale(1.1);
}

.color-option.active {
  border-color: #333;
  box-shadow: 0 0 0 2px #fff, 0 0 0 4px #333;
}

.color-option i {
  color: white;
  font-size: 16px;
  text-shadow: 0 0 2px rgba(0,0,0,0.5);
}

.color-active-dot {
  width: 12px;
  height: 12px;
  background-color: white;
  border-radius: 50%;
  display: inline-block;
  box-shadow: 0 1px 2px rgba(0,0,0,0.2);
}

.custom-color-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.color-input-native {
  width: 36px;
  height: 36px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  cursor: pointer;
  padding: 2px;
  background: #fff;
}

.color-input-native::-webkit-color-swatch-wrapper {
  padding: 0;
}

.color-input-native::-webkit-color-swatch {
  border: none;
  border-radius: 2px;
}

.color-value {
  font-family: monospace;
  font-size: 12px;
  color: #606266;
}

.domain-input-wrapper {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 8px;
  background-color: #fff;
}

.domain-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
  min-height: 36px;
}

.domain-tag {
  display: inline-flex;
  align-items: center;
  background-color: #f4f4f5;
  border-radius: 4px;
  padding: 0 8px;
  font-size: 12px;
  line-height: 28px;
  color: #909399;
}

.domain-text {
  margin-right: 6px;
}

.domain-close {
  cursor: pointer;
  font-size: 12px;
  color: #909399;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  transition: all 0.2s;
}

.domain-close:hover {
  background-color: #c0c4cc;
  color: #fff;
}

.domain-placeholder {
  font-size: 12px;
  color: #c0c4cc;
  line-height: 28px;
}

.domain-add {
  display: flex;
  align-items: center;
  gap: 8px;
  border-top: 1px solid #e4e7ed;
  padding-top: 8px;
}

.domain-input {
  flex: 1;
  height: 28px;
  line-height: 28px;
  padding: 0 8px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 12px;
  outline: none;
  transition: border-color 0.2s;
}

.domain-input:focus {
  border-color: #409eff;
}

.domain-add-btn {
  height: 28px;
  line-height: 26px;
  padding: 0 12px;
  background-color: #409eff;
  border: 1px solid #409eff;
  border-radius: 4px;
  font-size: 12px;
  color: #fff;
  cursor: pointer;
  transition: all 0.2s;
}

.domain-add-btn:hover {
  background-color: #66b1ff;
  border-color: #66b1ff;
}

.domain-add-btn:active {
  background-color: #3a8ee6;
  border-color: #3a8ee6;
}

.preview-btn {
  padding: 6px 12px;
  border-radius: 4px;
  border: 1px solid;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.preview-btn:hover {
  opacity: 0.85;
}

.upload-container {
  position: relative;
  display: inline-block;
  width: 100%;
  height: 100%;
  border: 1px dashed #ccc;
  border-radius: 4px;
  background-color: #f5f5f5;
}

.file-input {
  display: none;
}

.upload-box {
  display: block;
  width: 100%;
  height: 60px;
  border: 2px dashed #474747;
  border-radius: 8px;
  background-color: rgba(240, 244, 249, 0.5);
  cursor: pointer;
  transition: all 0.3s;
  position: relative;
  overflow: hidden;
}

.upload-box:hover {
  border-color: #00f0ff;
  background-color: rgba(0, 240, 255, 0.05);
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
}

.plus-icon {
  font-size: 2rem;
  font-weight: lighter;
  margin-top: 15pt;
  margin-bottom: 0;
  color: #00f0ff;
}

.upload-text {
  margin-top: -8px;
  font-size: 0.6rem;
  margin-bottom: 8px;
}

.file-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 0 20px;
  position: relative;
}

.thumbnail-icon {
  max-width: 32px;
  max-height: 32px;
  object-fit: contain;
}

.thumbnail-banner {
  max-width: 600px;
  max-height: 80px;
  object-fit: contain;
}

.remove-btn {
  position: absolute;
  top: 0px;
  left: 20px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #ffffff;
  color: red;
  border: none;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.remove-btn:hover {
  background: #ff7875;
  color: white;
}

.remove-btn-banner {
  position: absolute;
  top: 0px;
  left: 390px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #ffffff;
  color: red;
  border: none;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.remove-btn-banner:hover {
  background: #ff7875;
  color: white;
}

.embed-code-container {
  text-align: center;
}

.embed-tip {
  text-align: left;
  margin-bottom: 12px;
  font-size: 13px;
  color: #606266;
}

.embed-code {
  background: #f5f7fa;
  padding: 16px;
  border-radius: 4px;
  overflow-x: auto;
  text-align: left;
  margin-bottom: 16px;
}

.embed-code code {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-all;
}

::v-deep .el-table .el-tooltip__popper {
  z-index: 9999 !important;
}
</style>